import { type PdvPermissionGroup, type PdvPermissionSubGroup, type PdvPermission, PdvPermissionDomain, PdvPermissionSubDomain, PDV_PERMISSION_DOMAIN_LABELS, PDV_PERMISSION_SUBDOMAIN_LABELS, PDV_PERMISSION_DOMAIN_MAPPING } from '../models/PdvPermission'
import { getGroupStats as getGenericGroupStats, getSubGroupStats as getGenericSubGroupStats, type PermissionStats } from '~/domain/dashboard/user/permissions/composables/usePermissionHierarchy'

/**
 * Agrupa permissões PDV por domínio e subdomínio
 */
export function groupPdvPermissionsByDomain (permissions: PdvPermission[]): PdvPermissionGroup[] {
  const domainGroups: Partial<Record<PdvPermissionDomain, Partial<Record<PdvPermissionSubDomain, PdvPermission[]>>>> = {}

  // Primeiro, agrupar por domínio e subdomínio
  permissions.forEach((permission) => {
    const { domain, subDomain } = permission

    if (!domainGroups[domain as PdvPermissionDomain]) {
      domainGroups[domain as PdvPermissionDomain] = {}
    }

    if (!domainGroups[domain as PdvPermissionDomain]![subDomain as PdvPermissionSubDomain]) {
      domainGroups[domain as PdvPermissionDomain]![subDomain as PdvPermissionSubDomain] = []
    }

    domainGroups[domain as PdvPermissionDomain]![subDomain as PdvPermissionSubDomain]!.push(permission)
  })

  // Converter para a estrutura final
  return Object.entries(domainGroups).map(([domain, subdomainGroups]) => {
    const domainKey = domain as PdvPermissionDomain

    // Criar subgrupos
    const subGroups: PdvPermissionSubGroup[] = Object.entries(subdomainGroups || {}).map(([subdomain, subPermissions]) => ({
      subDomain: subdomain as PdvPermissionSubDomain,
      label: PDV_PERMISSION_SUBDOMAIN_LABELS[subdomain as PdvPermissionSubDomain] || subdomain,
      permissions: subPermissions || []
    }))

    // Todas as permissões do domínio (flatten)
    const allDomainPermissions = Object.values(subdomainGroups || {}).flat().filter(Boolean)

    return {
      domain: domainKey,
      label: PDV_PERMISSION_DOMAIN_LABELS[domainKey] || 'Outros',
      permissions: allDomainPermissions,
      subGroups
    }
  })
}

/**
 * Calcula estatísticas simples para um grupo PDV (incluindo subgrupos)
 */
export function getPdvGroupStats (group: PdvPermissionGroup, selectedPermissions: string[]): PermissionStats {
  return getGenericGroupStats(group, selectedPermissions)
}

/**
 * Calcula estatísticas para um subgrupo PDV
 */
export function getPdvSubGroupStats (subGroup: PdvPermissionSubGroup, selectedPermissions: string[]): PermissionStats {
  return getGenericSubGroupStats(subGroup, selectedPermissions)
}

export function mapPdvPermissionToDomain (permissionName: string): PdvPermissionDomain {
  const name = permissionName.toLowerCase()
  // Procurar em qual domínio a permissão se encaixa
  for (const [domain, subdomains] of Object.entries(PDV_PERMISSION_DOMAIN_MAPPING)) {
    const domainKey = domain as PdvPermissionDomain
    if (subdomains.some((subdomain) => {
      // Mapear subdomínio para palavras-chave específicas
      switch (subdomain) {
        case PdvPermissionSubDomain.POS:
          return name.includes('pos')
        case PdvPermissionSubDomain.ATTENDANCE:
          return name === 'attendance'
        case PdvPermissionSubDomain.ATTENDANCE_OPERATIONS:
          return name.includes('attendance_') && !name.includes('payment')
        case PdvPermissionSubDomain.ATTENDANCE_PAYMENT:
          return name.includes('attendance_payment')
        case PdvPermissionSubDomain.SALES_OPERATIONS:
          return (name.includes('sale_') && (name.includes('devolution') || name.includes('cancel')))
        case PdvPermissionSubDomain.SALES_DISCOUNT:
          return name.includes('sale_discount')
        case PdvPermissionSubDomain.SALES_PAYMENT:
          return name.includes('update_sale_payment')
        case PdvPermissionSubDomain.SALES_VIEWING:
          return name.includes('sale_viewing')
        case PdvPermissionSubDomain.CUSTOMER_CREDIT:
          return name.includes('customer_credit')
        case PdvPermissionSubDomain.CUSTOMER_BILLING:
          return name.includes('customer_billing_setup')
        case PdvPermissionSubDomain.CASH_OPERATIONS:
          return name.includes('pick_change')
        case PdvPermissionSubDomain.GENERAL:
          return name.includes('configs') || name === 'print_resend'
        case PdvPermissionSubDomain.ADMINISTRATIVE:
          return name.includes('administrative_area')
        case PdvPermissionSubDomain.SECURITY:
          return name.includes('password_panel')
        case PdvPermissionSubDomain.CONSIGNMENT:
          return name.includes('consigned_order')
        default:
          return false
      }
    })) {
      return domainKey
    }
  }

  return PdvPermissionDomain.UNKNOWN
}

export function mapPdvPermissionToSubDomain (permissionName: string): PdvPermissionSubDomain {
  const name = permissionName.toLowerCase()

  // Mapeamento direto de palavras-chave para subdomínio
  if (name.includes('pos')) { return PdvPermissionSubDomain.POS }
  if (name === 'attendance') { return PdvPermissionSubDomain.ATTENDANCE }
  if (name.includes('attendance_') && !name.includes('payment')) { return PdvPermissionSubDomain.ATTENDANCE_OPERATIONS }
  if (name.includes('attendance_payment')) { return PdvPermissionSubDomain.ATTENDANCE_PAYMENT }
  if (name.includes('sale_') && (name.includes('devolution') || name.includes('cancel'))) { return PdvPermissionSubDomain.SALES_OPERATIONS }
  if (name.includes('sale_discount')) { return PdvPermissionSubDomain.SALES_DISCOUNT }
  if (name.includes('update_sale_payment')) { return PdvPermissionSubDomain.SALES_PAYMENT }
  if (name.includes('sale_viewing')) { return PdvPermissionSubDomain.SALES_VIEWING }
  if (name.includes('customer_credit')) { return PdvPermissionSubDomain.CUSTOMER_CREDIT }
  if (name.includes('customer_billing_setup')) { return PdvPermissionSubDomain.CUSTOMER_BILLING }
  if (name.includes('pick_change')) { return PdvPermissionSubDomain.CASH_OPERATIONS }
  if (name.includes('configs') || name === 'print_resend') { return PdvPermissionSubDomain.GENERAL }
  if (name.includes('administrative_area')) { return PdvPermissionSubDomain.ADMINISTRATIVE }
  if (name.includes('password_panel')) { return PdvPermissionSubDomain.SECURITY }
  if (name.includes('consigned_order')) { return PdvPermissionSubDomain.CONSIGNMENT }

  return PdvPermissionSubDomain.GENERAL_PDV // fallback
}
